/**
 * Все обращения к API одним слоем: экраны не знают про URL и заголовки.
 * Порт `Services/APIRepository.swift` — набор методов держим одинаковым
 * с iOS, иначе клиенты незаметно разъедутся по возможностям.
 */
import type {
  Auction,
  CategoryNode,
  Collection,
  CollectionItem,
  ForumSection,
  MatchPartner,
  Page,
  Publisher,
  Session,
  TradeOffer,
  UserProfile
} from '../model/types'
import { request } from './client'

/// Пустые параметры не должны давать хвост «?» — сервер его стерпит, но
/// адреса в логах и кеше разъедутся на пустом месте.
function queryString(params: URLSearchParams | string): string {
  const text = typeof params === 'string' ? params : params.toString()
  if (text.length === 0) return ''
  return text.startsWith('?') ? text : `?${text}`
}

export const api = {
  // MARK: - Аккаунт
  login: (login: string, password: string) =>
    request<Session>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ login, password })
    }),
  register: (login: string, email: string, password: string) =>
    request<Session>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ login, email, password })
    }),
  me: () => request<UserProfile>('/auth/me'),
  /// Правка своего профиля из кабинета. PATCH, а не PUT: кабинет шлёт
  /// только изменённые поля, остальное сервер не трогает.
  updateMe: (patch: Partial<Pick<UserProfile, 'displayName' | 'city'>>) =>
    request<UserProfile>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(patch)
    }),

  // MARK: - Каталог
  categories: () => request<CategoryNode[]>('/categories'),
  publishers: () => request<Publisher[]>('/publishers'),
  /// Фильтры собираются вызывающим: экран каталога держит их в адресной
  /// строке, и ему удобнее отдать готовые параметры, чем раскладывать
  /// их по аргументам и собирать обратно здесь.
  collections: (params: URLSearchParams | string = '') =>
    request<Page<Collection>>(`/collections${queryString(params)}`),
  collection: (slug: string) =>
    request<Collection>(`/collections/${slug}`),
  collectionItems: (slug: string) =>
    request<CollectionItem[]>(`/collections/${slug}/items`),

  // MARK: - Инвентарь
  myCollections: () => request<Collection[]>('/inventory/collections'),
  duplicates: () => request<CollectionItem[]>('/inventory/duplicates'),
  wanted: () => request<CollectionItem[]>('/inventory/wanted'),
  updateItem: (itemId: string, haveCount: number, isWanted: boolean) =>
    request<void>('/inventory/items', {
      method: 'PUT',
      body: JSON.stringify({ itemId, haveCount, isWanted })
    }),

  // MARK: - Обмен
  partners: (query = '') =>
    request<MatchPartner[]>(`/matching/partners${query}`),
  trades: () => request<TradeOffer[]>('/trades'),
  trade: (id: string) => request<TradeOffer>(`/trades/${id}`),

  // MARK: - Прочее
  auctions: () => request<Auction[]>('/auctions'),
  forumSections: () => request<ForumSection[]>('/forum/sections')
}
